import type { ComponentType, SVGProps } from 'react'
import { Link } from 'react-router-dom'
import { ChatHistory, DraftSparkle, FileClock, MedChron, MedicalBillsNew, Table } from '@/assets/icons'
import TruncatedText from '@/components/truncated-text/TruncatedText'
import { kNewConversationTitle } from '@/chat-common/conversation_list/constants'
import { kUntitledDraftName } from '@/drafting/constants'
import { formatMatterActivityTime } from '@/matter/utils/format-activity-time'
import { ActivityEventType, MatterRecordType, isDraftRow, type ActivityFeedRow } from '../schemas'

const recordLabels: Record<MatterRecordType, string> = {
  [MatterRecordType.MedChron]: 'Medical chronology',
  [MatterRecordType.MedSummary]: 'Billing summary',
  [MatterRecordType.Timeline]: 'Timeline',
}

const recordIcons: Record<MatterRecordType, ComponentType<SVGProps<SVGSVGElement>>> = {
  [MatterRecordType.MedChron]: MedChron,
  [MatterRecordType.MedSummary]: MedicalBillsNew,
  [MatterRecordType.Timeline]: FileClock,
}

function getIcon(row: ActivityFeedRow) {
  switch (row.event) {
    case ActivityEventType.ChatUpdated:
      return ChatHistory
    case ActivityEventType.DraftGenerated:
    case ActivityEventType.DraftUpdated:
      return DraftSparkle
    case ActivityEventType.TableCreated:
    case ActivityEventType.TableUpdated:
      return Table
    case ActivityEventType.RecordGenerated:
    case ActivityEventType.RecordUpdated:
      return recordIcons[row.record_type]
    default: {
      const _exhaustive: never = row
      return _exhaustive
    }
  }
}

function getTitle(row: ActivityFeedRow) {
  switch (row.event) {
    case ActivityEventType.ChatUpdated:
      return row.conversation_title ?? kNewConversationTitle
    case ActivityEventType.DraftGenerated:
    case ActivityEventType.DraftUpdated:
      return row.draft_title ?? kUntitledDraftName
    case ActivityEventType.TableCreated:
    case ActivityEventType.TableUpdated:
      return row.table_name
    case ActivityEventType.RecordGenerated:
    case ActivityEventType.RecordUpdated:
      return recordLabels[row.record_type]
    default: {
      const _exhaustive: never = row
      return _exhaustive
    }
  }
}

/** What the row opens, for the screen reader label on the full-row button. */
function getOpenLabel(row: ActivityFeedRow) {
  switch (row.event) {
    case ActivityEventType.ChatUpdated:
      return 'chat'
    case ActivityEventType.DraftGenerated:
    case ActivityEventType.DraftUpdated:
      return 'draft'
    case ActivityEventType.TableCreated:
    case ActivityEventType.TableUpdated:
      return 'table'
    case ActivityEventType.RecordGenerated:
    case ActivityEventType.RecordUpdated:
      return 'matter record'
    default: {
      const _exhaustive: never = row
      return _exhaustive
    }
  }
}

/** The sentence after the actor's name, or the whole sentence in a matter the actor is alone in. */
function getActivityCopy(row: ActivityFeedRow, hasActor: boolean) {
  switch (row.event) {
    case ActivityEventType.ChatUpdated:
      return hasActor ? 'worked in this chat' : 'Chat updated'
    case ActivityEventType.DraftGenerated:
      return hasActor ? 'worked on a draft' : 'Draft created'
    case ActivityEventType.DraftUpdated:
      return hasActor ? 'worked on a draft' : 'Draft updated'
    case ActivityEventType.TableCreated:
      return hasActor ? 'created a table' : 'Table created'
    case ActivityEventType.TableUpdated:
      return hasActor ? 'updated a table' : 'Table updated'
    case ActivityEventType.RecordGenerated: {
      const files = `${row.file_count} ${row.file_count === 1 ? 'file' : 'files'}`
      return hasActor ? `generated a matter record from ${files}` : `Matter record generated from ${files}`
    }
    case ActivityEventType.RecordUpdated:
      return hasActor ? 'updated a matter record' : 'Matter record updated'
    default: {
      const _exhaustive: never = row
      return _exhaustive
    }
  }
}

/** The "in [Chat Name]" tail of a draft row, linked when the feed knows where that chat lives. */
function OriginatingChat({ title, path, onOpen }: { title: string; path?: string; onOpen: () => void }) {
  const name = (
    <>
      <ChatHistory aria-hidden className="size-3.5 shrink-0" />
      {/* The tooltip toggles on tap and would swallow the link's own click. */}
      <TruncatedText disableOnTouch>{title}</TruncatedText>
    </>
  )

  return (
    <span className="flex min-w-0 items-center gap-0.5">
      <span className="shrink-0">in</span>
      {path ? (
        <Link
          to={path}
          aria-label={`Open originating chat: ${title}`}
          className="pointer-events-auto relative z-[1] flex min-w-0 items-center gap-0.5 text-brand-700 underline focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500"
        >
          {name}
        </Link>
      ) : (
        <span className="pointer-events-auto relative z-[1] flex min-w-0 items-center gap-0.5" onClick={onOpen}>
          {name}
        </span>
      )}
    </span>
  )
}

export function ActivityFeedListItem({
  row,
  actorLabel,
  conversationPath,
  onOpen,
}: {
  row: ActivityFeedRow
  /** The actor's display name, already resolved to "You" or a member name. Absent in a matter of one. */
  actorLabel?: string
  /** Destination of the "in [Chat Name]" link on a draft row. */
  conversationPath?: string
  onOpen: () => void
}) {
  const Icon = getIcon(row)
  const title = getTitle(row)
  const hasChatTail = isDraftRow(row)
  return (
    <li className="relative flex h-[72px] min-w-0 cursor-pointer items-start gap-3 overflow-hidden border-b border-brand-neutral-200 px-0.5 py-4 hover-capable:hover:bg-brand-neutral-100">
      <button
        type="button"
        aria-label={`Open ${getOpenLabel(row)}: ${title}`}
        onClick={onOpen}
        className="absolute inset-0 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500"
      />
      <Icon aria-hidden className="pointer-events-none mt-0.5 size-[18px] shrink-0 text-brand-neutral-950" />
      <div className="pointer-events-none min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <TruncatedText
            onClick={onOpen}
            // Mouse-only twin of the row button: the content sits under it, so hover only reaches text that
            // takes pointer events back, and taking them back means taking the click too. A span rather than
            // a button, so the row announces one control rather than three.
            disableOnTouch
            className="pointer-events-auto relative z-[1] text-left font-rethink-sans text-sm font-semibold leading-5 text-brand-neutral-950"
          >
            {title}
          </TruncatedText>
          <span className="ml-auto mt-0.5 shrink-0 self-start text-2xs text-brand-neutral-800">{formatMatterActivityTime(row.occurred_at)}</span>
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-1 text-xs font-medium leading-4 text-brand-neutral-800">
          {actorLabel && (
            // Capped out here rather than on the text: on touch the tooltip renders a full-width wrapper
            // around its child, so a cap on the child never reaches the flex layout. A third of the line
            // is the name's share whatever else is on it, and the chat name takes what is left.
            <span className="pointer-events-auto relative z-[1] max-w-[35%] shrink-0">
              <TruncatedText onClick={onOpen} disableOnTouch className="font-semibold text-brand-neutral-950">
                {actorLabel}
              </TruncatedText>
            </span>
          )}
          {/* Truncating mid-sentence is unreadable, so the words only give way when nothing follows them. */}
          <span className={hasChatTail ? 'shrink-0' : 'min-w-0 truncate'}>{getActivityCopy(row, Boolean(actorLabel))}</span>
          {isDraftRow(row) && <OriginatingChat title={row.conversation_title ?? kNewConversationTitle} path={conversationPath} onOpen={onOpen} />}
        </div>
      </div>
    </li>
  )
}
